import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { OntologyTree } from '@/components/OntologyTree';

export default function OntologyGraphPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const diseaseParam = searchParams.get('disease') ?? '';
  const ontoIDParam: 'id' | 'name' = searchParams.get('ontoID') === 'name' ? 'name' : 'id';
  const [query, setQuery] = useState(diseaseParam);
  const [searchMode, setSearchMode] = useState<'id' | 'name'>(ontoIDParam);
  const rootNodeId = diseaseParam || undefined;

  const currentParams = useMemo(
    () => ({ disease: diseaseParam, ontoID: ontoIDParam }),
    [diseaseParam, ontoIDParam],
  );

  useEffect(() => {
    setQuery(diseaseParam);
    setSearchMode(ontoIDParam);
  }, [diseaseParam, ontoIDParam]);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanedQuery = query.trim();
    const nextParams = new URLSearchParams(searchParams);
    if (cleanedQuery) {
      nextParams.set('disease', cleanedQuery);
      nextParams.set('ontoID', searchMode);
    } else {
      nextParams.delete('disease');
      nextParams.delete('ontoID');
    }
    setSearchParams(nextParams);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>DISGENET: Ontology Explorer</title>
        <meta name="description" content="Interactive disease ontology tree explorer." />
      </Helmet>

      <header
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid #e0e8f0',
          background: '#fff',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              color: '#1a2e44',
            }}
          >
            Ontology Explorer
          </h1>
          {currentParams.disease && (
            <span
              style={{
                fontSize: 13,
                color: '#6b7a8d',
                fontWeight: 400,
              }}
            >
              Root ({currentParams.ontoID}):{' '}
              <code style={{ fontSize: 12, color: '#4a90d9' }}>{currentParams.disease}</code>
            </span>
          )}
        </div>

        <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={searchMode}
            onChange={e => setSearchMode(e.target.value === 'name' ? 'name' : 'id')}
            aria-label="Search mode"
            style={{
              height: 36,
              border: '1px solid #c8d6e5',
              borderRadius: 8,
              padding: '0 10px',
              background: '#fff',
              color: '#1a2e44',
            }}
          >
            <option value="id">By ID</option>
            <option value="name">By name</option>
          </select>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={searchMode === 'id' ? 'e.g. D020195 or C0038454' : 'e.g. Alzheimer disease'}
            style={{
              width: 260,
              maxWidth: '48vw',
              height: 36,
              border: '1px solid #c8d6e5',
              borderRadius: 8,
              padding: '0 12px',
              fontSize: 13,
            }}
          />
          <button
            type="submit"
            style={{
              height: 36,
              border: '1px solid #4a90d9',
              borderRadius: 8,
              background: '#4a90d9',
              color: '#fff',
              padding: '0 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Search
          </button>
        </form>
      </header>

      <main style={{ flex: 1, minHeight: 0 }}>
        <OntologyTree rootNodeId={rootNodeId} rootNodeType={currentParams.ontoID} />
      </main>
    </div>
  );
}
